import React, { useEffect } from 'react';
import { 
    X, 
    Clock, 
    Calendar, 
    User, 
    Zap, 
    Activity, 
    ArrowRightCircle, 
    CheckCircle2,
    Hash,
    Briefcase,
    AlertTriangle,
    ShieldAlert,
    Printer,
    Sparkles
} from 'lucide-react';

const formatTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
};

const formatDuration = (mins) => {
    if (!mins && mins !== 0) return '-';
    const h = Math.floor(Math.abs(mins) / 60);
    const m = Math.abs(mins) % 60;
    return `${mins < 0 ? '-' : ''}${h}sa ${m}dk`;
};

const getLocalDateKey = (dateVal) => {
    if (!dateVal) return '';
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getDayOffsetBadge = (targetDateVal, baseDateVal) => {
    const targetKey = getLocalDateKey(targetDateVal);
    const baseKey = getLocalDateKey(baseDateVal);
    if (!targetKey || !baseKey || targetKey === baseKey) return null;
    const t = new Date(`${targetKey}T00:00:00`);
    const b = new Date(`${baseKey}T00:00:00`);
    const diffDays = Math.round((t - b) / (1000 * 60 * 60 * 24));
    if (diffDays === 1) {
        return { label: '+1 Gün', cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' };
    }
    if (diffDays === -1) {
        return { label: '-1 Gün', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' };
    }
    return { label: targetKey, cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' };
};

const AttendanceDayModal = ({ isOpen, onClose, data, loading, onOpenQualityDrawer }) => {
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
            document.body.style.cursor = 'default';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handlePrint = () => {
        if (!data) return;
        const dateFormatted = new Date(data.date).toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            weekday: 'long'
        });
        const rowsHtml = (data.transitions || []).map((t) => `
            <tr>
                <td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-family:monospace;">${formatTime(t.recordTime)}</td>
                <td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-weight:bold;">${t.entryExitType || '-'}</td>
                <td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;">${t.deviceName || '-'}</td>
                <td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;text-align:right;">${t.isDeclare ? 'Beyan / Manuel' : 'Otomatik'}</td>
            </tr>
        `).join('');

        const printWin = window.open('', '_blank', 'width=820,height=700');
        if (!printWin) {
            window.print();
            return;
        }
        printWin.document.write(`
            <!DOCTYPE html>
            <html lang="tr">
            <head>
                <meta charset="utf-8" />
                <title>Günlük Puantaj Raporu - ${data.employeeFullName || ''}</title>
                <style>
                    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0f172a; padding: 28px; }
                    .header { border-bottom: 2px solid #4f46e5; padding-bottom: 14px; margin-bottom: 20px; display:flex; justify-content:space-between; align-items:flex-end; }
                    .title { font-size: 20px; font-weight: 800; margin: 0; }
                    .subtitle { font-size: 13px; color: #475569; margin-top: 4px; }
                    .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
                    .card { border: 1px solid #cbd5e1; border-radius: 10px; padding: 12px; background: #f8fafc; }
                    .card-label { font-size: 10px; text-transform: uppercase; color: #64748b; font-weight: 700; }
                    .card-val { font-size: 16px; font-weight: 800; margin-top: 4px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px; }
                    th { background: #f1f5f9; text-align: left; padding: 8px 12px; font-size: 11px; text-transform: uppercase; color: #475569; border-bottom: 1px solid #cbd5e1; }
                </style>
            </head>
            <body>
                <div class="header">
                    <div>
                        <h1 class="title">${data.employeeFullName || 'Personel'} (#${data.employeeId || ''})</h1>
                        <div class="subtitle">${dateFormatted} · Günlük Puantaj & Hareket Detayı</div>
                    </div>
                    <div style="font-size:11px;color:#64748b;">Yazdırma: ${new Date().toLocaleString('tr-TR')}</div>
                </div>
                <div class="grid">
                    <div class="card"><div class="card-label">Durum</div><div class="card-val">${data.status || '-'}</div></div>
                    <div class="card"><div class="card-label">Vardiya</div><div class="card-val">${data.shiftCode || '-'} (${data.shiftName || '-'})</div></div>
                    <div class="card"><div class="card-label">Net Süre</div><div class="card-val">${formatDuration(data.netMinutes || 0)}</div></div>
                    <div class="card"><div class="card-label">Brüt Çalışma</div><div class="card-val">${formatDuration(data.actualWorkDurationMinutes || 0)}</div></div>
                </div>
                <div class="grid">
                    <div class="card"><div class="card-label">Planlanan Giriş / Çıkış</div><div class="card-val">${formatTime(data.plannedStartTime)} - ${formatTime(data.plannedEndTime)}</div></div>
                    <div class="card"><div class="card-label">Fiili İlk Giriş / Son Çıkış</div><div class="card-val">${formatTime(data.firstEntryTime)} - ${formatTime(data.lastExitTime)}</div></div>
                    <div class="card"><div class="card-label">Fazla Mesai</div><div class="card-val">${formatDuration(data.overtimeMinutes || 0)}</div></div>
                    <div class="card"><div class="card-label">Eksik Süre</div><div class="card-val">${formatDuration(data.undertimeMinutes || 0)}</div></div>
                </div>
                <h3 style="font-size:13px;text-transform:uppercase;color:#334155;margin-top:24px;">Hareket Kayıtları (${(data.transitions || []).length})</h3>
                <table>
                    <thead>
                        <tr>
                            <th>Saat</th>
                            <th>Yön</th>
                            <th>Terminal / Cihaz</th>
                            <th style="text-align:right;">Kaynak</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rowsHtml || '<tr><td colspan="4" style="padding:16px;text-align:center;color:#94a3b8;">Hareket kaydı bulunamadı.</td></tr>'}
                    </tbody>
                </table>
                <script>
                    window.onload = function() { window.print(); };
                </script>
            </body>
            </html>
        `);
        printWin.document.close();
    };

    return (
        <div className="fixed inset-0 z-[100] flex justify-center items-start overflow-y-auto p-4 md:p-8">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose}></div>
            
            <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 transform transition-all animate-in fade-in zoom-in duration-300 my-auto">
                
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10 rounded-t-2xl">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                            {data?.employeeFullName ? <span className="font-bold text-lg">{data.employeeFullName.substring(0, 1)}</span> : <User size={20} />}
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white leading-none">
                                {loading ? 'Yükleniyor...' : (data?.employeeFullName || 'Gün Detayı')}
                            </h3>
                            <div className="flex items-center gap-3 mt-1.5 text-gray-500 dark:text-gray-400 text-xs font-medium">
                                <div className="flex items-center gap-1.5">
                                    <Calendar size={12} />
                                    <span>{data ? new Date(data.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' }) : '-'}</span>
                                </div>
                                <span className="text-gray-300 dark:text-gray-600">•</span>
                                <div className="flex items-center gap-1.5">
                                    <Hash size={12} />
                                    <span>#{data?.employeeId}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title="Kapat"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-6 space-y-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <div className="w-10 h-10 border-4 border-indigo-600/20 border-t-indigo-600 rounded-full animate-spin"></div>
                            <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Detaylar Hazırlanıyor</span>
                        </div>
                    ) : data ? (
                        <>
                            {/* Key Stats Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <StatCard label="Durum" value={data.status} subStatus="Günlük Statü" color="indigo" icon={Zap} />
                                <StatCard label="Vardiya" value={data.shiftCode} subStatus={data.shiftName} color="slate" icon={Briefcase} />
                                <StatCard label="Net Süre" value={formatDuration(data.netMinutes)} subStatus="Hakediş" color={data.netMinutes < 0 ? 'rose' : 'emerald'} icon={CheckCircle2} />
                                <StatCard label="Puantaj" value={formatDuration(data.actualWorkDurationMinutes)} subStatus="Brüt Çalışma" color="sky" icon={Clock} />
                            </div>

                            {/* Detailed Rows */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50 space-y-3">
                                    <h4 className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                        <Clock size={14} className="text-indigo-500" /> Zaman Planlaması
                                    </h4>
                                    <div className="space-y-2">
                                        <TimeRow label="Planlanan Giriş" value={formatTime(data.plannedStartTime)} isDanger={data.lateEntryMinutes > 0} />
                                        <TimeRow label="Planlanan Çıkış" value={formatTime(data.plannedEndTime)} isDanger={data.earlyExitMinutes > 0} />
                                        <div className="h-px bg-gray-200 dark:bg-gray-700 my-1.5"></div>
                                        <TimeRow label="İlk Okuma" value={formatTime(data.firstEntryTime)} isBold isDanger={data.lateEntryMinutes > 0} />
                                        <TimeRow label="Son Okuma" value={formatTime(data.lastExitTime)} isBold isDanger={data.earlyExitMinutes > 0} />
                                    </div>
                                </div>

                                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50 space-y-3">
                                    <h4 className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                        <Activity size={14} className="text-indigo-500" /> Performans & Sapma
                                    </h4>
                                    <div className="space-y-2">
                                        <DurationRow label="Geç Giriş" value={data.lateEntryMinutes} />
                                        <DurationRow label="Erken Çıkış" value={data.earlyExitMinutes} />
                                        <DurationRow label="Fazla Mesai" value={data.overtimeMinutes} isPositive />
                                        <DurationRow label="Eksik Süre" value={data.undertimeMinutes} isNegative />
                                    </div>
                                </div>
                            </div>

                            {/* Transition Logs */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wide flex items-center gap-2">
                                        <ArrowRightCircle size={14} className="text-indigo-500" /> Hareket Kayıtları
                                    </h4>
                                    <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700">
                                        {data.transitions?.length || 0} Kayıt
                                    </span>
                                </div>

                                {/* Seçenek 2: Önceki Günün Gece Mesaisine Ait (Kilitli/Bilgi Amaçlı) Hareketler */}
                                {Array.isArray(data.previousDayTransitions) && data.previousDayTransitions.length > 0 && (
                                    <div className="p-3 rounded-xl bg-slate-100/80 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-700 space-y-2 opacity-85">
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <span className="text-[10px] font-black uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                                                🌙 Önceki Gün Mesaisine Ait Hareket (Kilitli)
                                            </span>
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-100/80 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                                                {data.previousDayTransitions[0]?.previousShiftDate} Puantajına İşlendi
                                            </span>
                                        </div>
                                        {data.previousDayTransitions.map((pt, idx) => (
                                            <div key={idx} className="flex flex-col gap-1 p-2 rounded-lg bg-white/80 dark:bg-gray-900/60 border border-slate-200/70 dark:border-slate-700/60 text-xs">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                            {pt.entryExitType}
                                                        </span>
                                                        <span className="font-mono font-bold text-slate-700 dark:text-slate-200">
                                                            {formatTime(pt.recordTime)}
                                                        </span>
                                                        {pt.previousShiftRange && (
                                                            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                                                                ({pt.previousShiftDate} · {pt.previousShiftRange})
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-[10px] text-slate-500 dark:text-slate-400">
                                                        {pt.deviceName || 'Cihaz'}
                                                    </span>
                                                </div>
                                                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                                                    ℹ️ Bu hareket <strong>{pt.previousShiftDate}</strong> gece mesaisinin devamı olduğu için o günün puantajında hesaplanmıştır; bu günün mesaisine dahil edilmez.
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm shadow-gray-200/50 dark:shadow-none">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                                                <th className="px-4 py-2.5 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Saat</th>
                                                <th className="px-4 py-2.5 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">İşlem</th>
                                                <th className="px-4 py-2.5 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cihaz</th>
                                                <th className="px-4 py-2.5 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Kaynak</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                            {data.transitions?.map((t, i) => {
                                                const dayBadge = getDayOffsetBadge(t.recordTime, data.date);
                                                return (
                                                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                        <td className="px-4 py-2.5 text-xs font-medium font-mono text-gray-900 dark:text-gray-200">
                                                            <div className="flex items-center gap-1.5">
                                                                <span>{formatTime(t.recordTime)}</span>
                                                                {dayBadge && (
                                                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${dayBadge.cls}`}>
                                                                        {dayBadge.label}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-2.5">
                                                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide ${t.entryExitType === 'Giriş' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'}`}>
                                                                {t.entryExitType}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-300 truncate max-w-[140px]" title={t.deviceName}>
                                                            {t.deviceName || '-'}
                                                        </td>
                                                        <td className="px-4 py-2.5 text-right">
                                                            <span className="inline-flex items-center text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                                {t.isDeclare ? 'Beyan' : 'Oto'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {(!data.transitions || data.transitions.length === 0) && (
                                                <tr>
                                                    <td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-xs italic bg-gray-50 dark:bg-gray-800/10">
                                                        Hareket kaydı bulunamadı.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            
                            {/* Quality Inspection Section */}
                            {data.id && (() => {
                                const qs = data.qualityStatus || 0;
                                const hasIssue = qs === 1 || qs === 2;
                                const isError = qs === 2;
                                const isAccepted = qs === 3;
                                
                                const bannerCls = hasIssue
                                    ? (isError 
                                        ? 'border-rose-200 bg-rose-50/80 dark:border-rose-900/40 dark:bg-rose-900/10'
                                        : 'border-amber-200 bg-amber-50/80 dark:border-amber-900/40 dark:bg-amber-900/10')
                                    : 'border-indigo-100 bg-indigo-50/50 dark:border-indigo-900/30 dark:bg-indigo-900/10';
                                    
                                const iconCls = hasIssue
                                    ? (isError
                                        ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-400'
                                        : 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400')
                                    : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400';

                                const statusLabel = qs === 2 ? 'Kritik Hata' : qs === 1 ? 'Uyarı' : qs === 3 ? 'Kabul Edildi' : 'Normal';
                                const statusBadgeCls = qs === 2 
                                    ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400'
                                    : qs === 1
                                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                                        : isAccepted
                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
                                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400';

                                return (
                                    <div className={`mt-4 p-4 rounded-xl border ${bannerCls} flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3`}>
                                        <div className="flex items-start gap-3">
                                            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconCls}`}>
                                                <ShieldAlert size={20} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">Puantaj Kalite & Akıllı Çözüm Merkezi</h4>
                                                    <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusBadgeCls}`}>
                                                        {statusLabel}
                                                    </span>
                                                </div>
                                                {(() => {
                                                    if (!data.qualityDetails) {
                                                        return (
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                                {hasIssue ? 'Kalite sorunu tespit edildi. Akıllı önerileri görmek ve müdahale etmek için açın.' : 'Hareket yönü değiştirme, tekil kayıt yok sayma veya manuel geçiş ekleme için açabilirsiniz.'}
                                                            </p>
                                                        );
                                                    }
                                                    let issues = [];
                                                    try {
                                                        const parsed = typeof data.qualityDetails === 'string'
                                                            ? JSON.parse(data.qualityDetails)
                                                            : data.qualityDetails;
                                                        issues = Array.isArray(parsed) ? parsed : [];
                                                    } catch {
                                                        return (
                                                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                                                                {data.qualityDetails}
                                                            </p>
                                                        );
                                                    }
                                                    if (issues.length === 0) {
                                                        return (
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                                Hareket yönü değiştirme, tekil kayıt yok sayma veya manuel geçiş ekleme için inceleyin.
                                                            </p>
                                                        );
                                                    }
                                                    return (
                                                        <ul className="mt-2 space-y-1.5">
                                                            {issues.map((issue, idx) => {
                                                                const severity = issue.Severity ?? issue.severity;
                                                                const isCritical = severity === 2 || severity === '2' || severity === 'Error';
                                                                const type = issue.Type || issue.type || issue.Code || issue.code || '';
                                                                const typeLabels = {
                                                                    ConsecutiveEntry: 'Ardışık Giriş',
                                                                    ConsecutiveExit: 'Ardışık Çıkış',
                                                                    MissingEntry: 'Eksik Giriş',
                                                                    MissingExit: 'Eksik Çıkış',
                                                                    ExitWithoutEntry: 'Eksik Giriş',
                                                                    EntryExitCountMismatch: 'Giriş / Çıkış Sayısı Uyumsuz',
                                                                    LeaveConflictWithPass: 'İzinli Günde Geçiş Çakışması',
                                                                    NoTransactionsFound: 'Hareket Kaydı Yok'
                                                                };
                                                                const label = typeLabels[type];
                                                                const message = issue.Message || issue.message || JSON.stringify(issue);
                                                                return (
                                                                    <li
                                                                        key={idx}
                                                                        className={`flex items-start gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium ${
                                                                            isCritical
                                                                                ? 'bg-rose-100/70 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'
                                                                                : 'bg-amber-100/70 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                                                        }`}
                                                                    >
                                                                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                                                        <span>{label ? `${label}: ${message}` : message}</span>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                if (onOpenQualityDrawer) {
                                                    onOpenQualityDrawer(data.id);
                                                    onClose();
                                                }
                                            }}
                                            className={`shrink-0 inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold shadow-sm border transition-all ${
                                                hasIssue
                                                    ? (isError 
                                                        ? 'bg-rose-600 text-white border-rose-600 hover:bg-rose-700'
                                                        : 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600')
                                                    : 'bg-white text-indigo-700 border-slate-200 hover:bg-indigo-50 hover:border-indigo-200 dark:bg-slate-800 dark:text-indigo-400 dark:border-slate-700 dark:hover:bg-slate-700'
                                            }`}
                                        >
                                            <Sparkles size={13} />
                                            {hasIssue ? 'Çözüm Merkezini Aç' : 'Kalite & Müdahale'}
                                        </button>
                                    </div>
                                );
                            })()}

                        </>
                    ) : (
                        <div className="py-12 text-center text-gray-500 italic">Veri bulunamadı.</div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 flex flex-wrap justify-between items-center gap-2 sticky bottom-0 border-t border-gray-100 dark:border-gray-700 rounded-b-2xl">
                    <div>
                        {data?.id && onOpenQualityDrawer && (
                            <button
                                type="button"
                                onClick={() => {
                                    onOpenQualityDrawer(data.id);
                                    onClose();
                                }}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded-xl border border-indigo-200/70 dark:border-indigo-800/50 transition-colors"
                            >
                                <Sparkles size={13} />
                                Akıllı Mantık & Hareket Müdahalesi
                            </button>
                        )}
                    </div>
                    <div className="flex items-center space-x-2.5">
                        <button 
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors duration-200"
                        >
                            Kapat
                        </button>
                        <button 
                            type="button"
                            onClick={handlePrint}
                            disabled={!data}
                            className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 rounded-xl transition-colors duration-200 shadow shadow-indigo-500/20"
                        >
                            <Printer size={14} />
                            Yazdır
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Sub-components for local reuse ---

const StatCard = ({ label, value, subStatus, color, icon: Icon }) => {
    const colors = {
        indigo: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border-indigo-100 dark:border-indigo-900/30',
        emerald: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-100 dark:border-green-900/30',
        rose: 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border-rose-100 dark:border-rose-900/30',
        sky: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-100 dark:border-blue-900/30',
        slate: 'bg-gray-50 dark:bg-gray-800/40 border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300'
    };

    return (
        <div className={`p-3 rounded-xl border ${colors[color] || colors.slate}`}>
            <div className="flex items-center justify-between mb-1.5 opacity-80">
                <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
                <Icon size={14} />
            </div>
            <div className="text-base font-bold truncate">{value || '-'}</div>
            <div className="text-[10px] opacity-70 truncate mt-0.5">{subStatus}</div>
        </div>
    );
};

const TimeRow = ({ label, value, isBold, isDanger }) => (
    <div className="flex justify-between items-center text-xs">
        <span className={`font-medium ${isDanger ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>{label}</span>
        <span className={`font-mono ${isDanger ? 'text-red-500 font-bold' : isBold ? 'font-bold text-gray-900 dark:text-gray-100 text-[13px]' : 'font-medium text-gray-700 dark:text-gray-300'}`}>{value}</span>
    </div>
);

const DurationRow = ({ label, value, isPositive, isNegative }) => (
    <div className="flex justify-between items-center text-xs">
        <span className="text-gray-500 dark:text-gray-400 font-medium">{label}</span>
        <span className={`font-mono px-1.5 py-0.5 rounded text-[11px] font-semibold ${isPositive ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' : isNegative ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
            {value > 0 ? (Math.floor(value / 60) + 's ' + (value % 60) + 'd') : '-'}
        </span>
    </div>
);

export default AttendanceDayModal;
